import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertTriangle, Eye, EyeOff, Diamond } from 'lucide-react';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';

/**
 * Partner login page component
 */
const PartnerLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = usePartnerAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await login(formData.email, formData.password);
      
      // Redirect to the page they tried to visit or dashboard
      const from = (location.state as any)?.from?.pathname || '/partner/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Invalid email or password');
    }
  };

  return (
    <div className="page-container" style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Partner Login</h1>
          <p style={subtitleStyle}>Sign in to access your partner dashboard</p>
        </div>

        {error && (
          <div style={errorBoxStyle}>
            <span style={errorIconStyle}><AlertTriangle size={16} /></span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label htmlFor="email" style={labelStyle}>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="partner@example.com"
              style={inputStyle}
              disabled={isLoading}
              required
            />
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="password" style={labelStyle}>
              Password
            </label>
            <div style={passwordContainerStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                style={inputStyle}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={passwordToggleStyle}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            style={submitButtonStyle}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={footerStyle}>
          <p style={footerTextStyle}>
            Don't have an account?{' '}
            <Link to="/partner/register" style={linkStyle}>
              Register here
            </Link>
          </p>
          <p style={footerTextStyle}>
            <Link to="/partner/forgot-password" style={linkStyle}>
              Forgot password?
            </Link>
          </p>
        </div>

        <div style={dividerStyle}>
          <span style={dividerTextStyle}>OR</span>
        </div>

        <button
          type="button"
          style={microsoftButtonStyle}
          disabled={isLoading}
          onClick={() => {
            // TODO: Implement Microsoft OIDC login
            setError('Microsoft login not yet implemented');
          }}
        >
          <span style={microsoftIconStyle}><Diamond size={16} /></span>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  minHeight: 'calc(100vh - 200px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f5f7fa',
  padding: '2rem',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  padding: '2.5rem',
  maxWidth: '450px',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '2rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  marginBottom: '0.5rem',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#7f8c8d',
};

const errorBoxStyle: React.CSSProperties = {
  backgroundColor: '#fee',
  border: '1px solid #fcc',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1.5rem',
  color: '#c33',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const errorIconStyle: React.CSSProperties = {
  fontSize: '1.2rem',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
};

const formGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#34495e',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  fontSize: '1rem',
  border: '1px solid #ddd',
  borderRadius: '6px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const passwordContainerStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const passwordToggleStyle: React.CSSProperties = {
  position: 'absolute',
  right: '0.75rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1.2rem',
  padding: '0.25rem',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.875rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3498db',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  marginTop: '0.5rem',
};

const footerStyle: React.CSSProperties = {
  marginTop: '1.5rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const footerTextStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#7f8c8d',
};

const linkStyle: React.CSSProperties = {
  color: '#3498db',
  textDecoration: 'none',
  fontWeight: '600',
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  margin: '1.5rem 0',
  textAlign: 'center',
};

const dividerTextStyle: React.CSSProperties = {
  padding: '0 1rem',
  color: '#95a5a6',
  fontSize: '0.85rem',
  fontWeight: '600',
  backgroundColor: 'white',
  position: 'relative',
  zIndex: 1,
  margin: '0 auto',
};

const microsoftButtonStyle: React.CSSProperties = {
  padding: '0.875rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: '#2c3e50',
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s, border-color 0.2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
};

const microsoftIconStyle: React.CSSProperties = {
  fontSize: '1.2rem',
};

export default PartnerLogin;

